<template>
  <!-- Desktop dialog -->
  <el-dialog
    v-if="!isMobile"
    :model-value="modelValue"
    :close-on-click-modal="false"
    width="1000px"
    :align-center="true"
    :show-close="false"
    class="music-select-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #header>
      <div class="dialog-header">
        <span class="dialog-title">选择背景音乐</span>
        <button class="btn-close" @click="$emit('update:modelValue', false)">
          <SvgIcon name="close-line" :size="24" />
        </button>
      </div>
    </template>

    <div class="category-tabs">
      <button
        v-for="cat in categories"
        :key="cat.value"
        class="category-tab"
        :class="{ active: activeCategory === cat.value }"
        @click="activeCategory = cat.value"
      >
        {{ cat.label }}
      </button>
    </div>

    <ScrollBar class="music-scroll" :item-gap="10" :scrollbar-offset="0">
      <MusicItem
        variant="no-music"
        name="不使用背景音乐"
        :selected="selectedId === NO_MUSIC"
        @click="selectMusic(NO_MUSIC)"
      />

      <MusicItem
        variant="upload"
        name="上传背景音乐"
        :selected="selectedId === UPLOAD_MUSIC"
        :hide-vip-icon="userStore.isPremiumUser"
        @click="selectMusic(UPLOAD_MUSIC)"
      />

      <MusicItem
        v-for="track in filteredTracks"
        :key="track.id"
        variant="track"
        :name="track.name"
        :duration="track.duration"
        :audio-url="track.url"
        :selected="selectedId === track.id"
        @click="selectMusic(track.id)"
      />
    </ScrollBar>

    <input
      ref="fileInputRef"
      type="file"
      accept="audio/*"
      style="display: none"
      @change="handleFileChange"
    />

    <div class="volume-control">
      <span class="volume-label">音量</span>
      <div class="volume-slider-wrapper">
        <el-slider
          v-model="localVolume"
          :min="0"
          :max="1"
          :step="0.01"
          :show-tooltip="false"
          class="volume-slider"
        />
      </div>
      <span class="volume-value">{{ Math.round(localVolume * 100) }}</span>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <button class="btn-cancel" @click="$emit('update:modelValue', false)">取消</button>
        <button class="btn-confirm" @click="handleConfirm">确定</button>
      </div>
    </template>
  </el-dialog>

  <!-- Mobile bottom sheet -->
  <Teleport to="body">
    <Transition name="music-sheet-fade">
      <div v-if="modelValue && isMobile" class="music-sheet-root">
        <div class="music-sheet-overlay" @click.self="closeDialog"></div>
        <div class="music-sheet" role="dialog" aria-label="背景音乐">
          <header class="music-sheet-header">
            <span class="music-sheet-title">背景音乐</span>
            <div class="music-sheet-header-actions">
              <button
                class="music-sheet-icon-btn"
                type="button"
                aria-label="搜索"
                @click="toggleSearch"
              >
                <SvgIcon name="search" :size="16" />
              </button>
              <button
                class="music-sheet-collapse"
                type="button"
                aria-label="关闭"
                @click="closeDialog"
              >
                <span class="music-sheet-collapse-icon" aria-hidden="true"></span>
              </button>
            </div>
          </header>

          <div v-if="searchVisible" class="music-sheet-search-bar">
            <SvgIcon name="search" :size="12" />
            <input
              ref="searchInputRef"
              v-model="searchKeyword"
              type="text"
              class="music-sheet-search-input"
              placeholder="搜索背景音乐"
            />
            <button
              v-if="searchKeyword"
              class="music-sheet-search-clear"
              type="button"
              aria-label="清除"
              @click="searchKeyword = ''"
            >
              <SvgIcon name="close-line" :size="14" />
            </button>
          </div>

          <div class="music-sheet-toolbar">
            <div class="music-sheet-tabs">
              <button
                v-for="cat in categories"
                :key="cat.value"
                class="music-sheet-tab"
                :class="{ active: activeCategory === cat.value }"
                type="button"
                @click="activeCategory = cat.value"
              >
                {{ cat.label }}
              </button>
            </div>
            <button class="music-sheet-upload" type="button" @click="selectMusic(UPLOAD_MUSIC)">
              <SvgIcon name="editor-upload" :size="12" />
              <span>背景音乐</span>
              <span
                v-if="!userStore.isPremiumUser"
                class="membership-badge membership-badge--expandable membership-badge--premium"
                aria-label="高级"
              >
                <img
                  class="membership-badge__compact"
                  :src="premiumCompactIcon"
                  alt=""
                  aria-hidden="true"
                />
                <img
                  class="membership-badge__full"
                  :src="premiumFullIcon"
                  alt=""
                  aria-hidden="true"
                />
              </span>
            </button>
          </div>

          <div class="music-sheet-list">
            <button
              class="music-sheet-item"
              :class="{ active: selectedId === NO_MUSIC }"
              type="button"
              @click="selectMusic(NO_MUSIC)"
            >
              <span class="music-sheet-no-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" class="music-sheet-no-svg">
                  <circle cx="12" cy="12" r="10" stroke="#160211" stroke-width="1.4" />
                  <line
                    x1="4"
                    y1="4"
                    x2="20"
                    y2="20"
                    stroke="#160211"
                    stroke-width="1.4"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
              <span class="music-sheet-item-name">无背景音乐</span>
            </button>

            <button
              v-for="track in mobileFilteredTracks"
              :key="track.id"
              class="music-sheet-item"
              :class="{ active: selectedId === track.id }"
              type="button"
              @click="selectMusic(track.id)"
            >
              <span class="music-sheet-track-icon" aria-hidden="true">
                <SvgIcon name="editor-music" :size="16" color="#ffffff" />
              </span>
              <span class="music-sheet-item-info">
                <span class="music-sheet-item-name">{{ track.name }}</span>
                <span class="music-sheet-item-duration">{{ track.duration }}</span>
              </span>
              <span
                class="music-sheet-play"
                :class="{ playing: playingTrackId === track.id }"
                @click.stop="toggleAudition(track)"
              >
                <svg
                  v-if="playingTrackId === track.id"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  aria-hidden="true"
                >
                  <rect x="6" y="5" width="4" height="14" rx="1" fill="#ffffff" />
                  <rect x="14" y="5" width="4" height="14" rx="1" fill="#ffffff" />
                </svg>
                <SvgIcon v-else name="play" :size="16" color="#6033E1" />
              </span>
            </button>

            <div
              v-if="searchVisible && searchKeyword && !mobileFilteredTracks.length"
              class="music-sheet-empty"
            >
              <span class="music-sheet-empty-text">无搜索结果</span>
            </div>
          </div>

          <div class="music-sheet-footer">
            <div class="music-sheet-slider-row">
              <span class="music-sheet-slider-label">音量</span>
              <div class="music-sheet-slider-track">
                <el-slider
                  v-model="localVolume"
                  :min="0"
                  :max="1"
                  :step="0.01"
                  :show-tooltip="false"
                  class="music-sheet-slider"
                />
              </div>
              <span class="music-sheet-slider-value">{{ Math.round(localVolume * 100) }}</span>
            </div>
            <div class="music-sheet-actions">
              <button class="music-sheet-btn-cancel" type="button" @click="closeDialog">
                取消
              </button>
              <button class="music-sheet-btn-confirm" type="button" @click="handleConfirm">
                确定
              </button>
            </div>
          </div>

          <input
            ref="fileInputRef"
            type="file"
            accept="audio/*"
            style="display: none"
            @change="handleFileChange"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import ScrollBar from '@/components/common/list/ScrollBar.vue';
  import MusicItem from '@/components/common/list/item/MusicItem.vue';
  import { fetchMaterialCategories, fetchMaterialList } from '@/api/material';
  import type { MaterialCategory, MaterialItem } from '@/api/material';
  import { uploadFile } from '@/api/upload';
  import AudioPlayer from '@/utils/AudioPlayer';
  import premiumCompactIcon from '@/assets/icons/svg/create-membership-premium.svg?url';
  import premiumFullIcon from '@/assets/icons/svg/create-membership-premium-full.svg?url';
  import { useUserStore } from '@/store';
  import { useUpgradeModal } from '@/hooks';

  export interface MusicTrack {
    id: string;
    name: string;
    duration: string;
    category: string;
    /** 音频资源 URL，为空则使用默认占位 */
    url?: string;
  }

  export interface MusicSelectResult {
    /** 选中的音乐 id；null 表示不使用音乐；'__upload__' 表示上传新音乐 */
    id: string | null;
    /** 音量 0~1 */
    volume: number;
    /** 上传的文件（仅 id === '__upload__' 时有值） */
    file?: File;
    /** 上传后返回的服务器 URL（仅 id === '__upload__' 时有值） */
    contentUrl?: string;
    /** 音轨名称（预设音轨或上传文件名） */
    trackName?: string;
    /** 已选择的 track 对象 */
    track?: MusicTrack;
  }

  const NO_MUSIC = null as unknown as string;
  const UPLOAD_MUSIC = '__upload__';

  const props = withDefaults(
    defineProps<{
      modelValue: boolean;
      /** 预设音轨列表 */
      tracks?: MusicTrack[];
      /** 弹窗打开时的初始音量（0~1），由父组件从 store 读取传入 */
      initialVolume?: number;
      /** 弹窗打开时当前 bgm 的 content URL，用于匹配预选音轨 */
      initialContentUrl?: string | null;
      /** 弹窗打开时当前 bgm 的展示名称，用于 URL 匹配失败时按名称匹配预选音轨 */
      initialDisplayName?: string;
    }>(),
    {
      tracks: () => [],
      initialVolume: 0.3,
      initialContentUrl: null,
      initialDisplayName: undefined
    }
  );

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'confirm', result: MusicSelectResult): void;
  }>();

  const fileInputRef = ref<HTMLInputElement | null>(null);
  const searchInputRef = ref<HTMLInputElement | null>(null);
  const isMobile = ref(false);
  const searchVisible = ref(false);
  const searchKeyword = ref('');
  const playingTrackId = ref<string | null>(null);

  const userStore = useUserStore();
  const { showUpgrade } = useUpgradeModal();

  const isMobileViewport = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

  // ============================================================
  // 分类（从 material.ts API 获取）
  // ============================================================

  const categories = ref<Array<{ label: string; value: string }>>([]);
  const activeCategory = ref('');
  const apiTracks = ref<MusicTrack[]>([]);

  function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  async function fetchCategories() {
    try {
      const list = await fetchMaterialCategories();
      categories.value = list.map((c: MaterialCategory) => ({
        label: c.name,
        value: c.code
      }));
      if (categories.value.length > 0 && !activeCategory.value) {
        activeCategory.value = categories.value[0].value;
      }
    } catch (e) {
      console.error('Failed to fetch music categories:', e);
    }
  }

  async function fetchMusicList(categoryCode: string) {
    try {
      const list = await fetchMaterialList(categoryCode);
      apiTracks.value = list.map((item: MaterialItem) => ({
        id: String(item.id),
        name: item.name,
        duration: formatDuration(item.duration),
        category: categoryCode,
        url: item.url
      }));
    } catch (e) {
      console.error('Failed to fetch music list:', e);
    }
  }

  const allTracks = computed(() => (props.tracks.length > 0 ? props.tracks : apiTracks.value));

  const filteredTracks = computed(() =>
    allTracks.value.filter((t) => t.category === activeCategory.value)
  );

  const mobileFilteredTracks = computed(() => {
    const keyword = searchKeyword.value.trim().toLowerCase();
    if (!keyword) return filteredTracks.value;
    return filteredTracks.value.filter((t) => t.name.toLowerCase().includes(keyword));
  });

  // 监听分类切换，拉取对应分类的音乐列表
  watch(activeCategory, (newCat) => {
    if (newCat) {
      fetchMusicList(newCat);
    }
  });

  // ============================================================
  // 选中状态
  // ============================================================

  const selectedId = ref<string | null>(null);
  const localVolume = ref(0.3);

  const stopAudition = () => {
    AudioPlayer.instance.stop();
    playingTrackId.value = null;
  };

  const toggleAudition = (track: MusicTrack) => {
    if (!track.url) return;

    if (playingTrackId.value === track.id) {
      stopAudition();
      return;
    }

    const reset = () => {
      playingTrackId.value = null;
    };
    AudioPlayer.instance.play(track.url, reset, reset);
    playingTrackId.value = track.id;
  };

  const toggleSearch = async () => {
    searchVisible.value = !searchVisible.value;
    if (!searchVisible.value) {
      searchKeyword.value = '';
      return;
    }
    await nextTick();
    searchInputRef.value?.focus();
  };

  const closeDialog = () => {
    stopAudition();
    searchVisible.value = false;
    searchKeyword.value = '';
    emit('update:modelValue', false);
  };

  // 弹窗打开时，加载分类和音乐列表，并初始化选中状态
  watch(
    () => props.modelValue,
    async (open) => {
      if (!open) {
        stopAudition();
        searchVisible.value = false;
        searchKeyword.value = '';
        return;
      }

      isMobile.value = isMobileViewport();
      localVolume.value = props.initialVolume ?? 0.3;
      searchVisible.value = false;
      searchKeyword.value = '';

      // 打开时才加载分类（内部会触发音乐列表加载）
      await fetchCategories();

      // 显式等待当前分类的音乐列表加载完成，确保匹配时列表已就绪
      if (activeCategory.value) {
        await fetchMusicList(activeCategory.value);
      }

      const contentUrl = props.initialContentUrl;
      const displayName = props.initialDisplayName;
      if (!contentUrl && !displayName) {
        selectedId.value = NO_MUSIC;
      } else {
        // 优先按 content URL 精确匹配
        let matched = allTracks.value.find((t) => t.url === contentUrl);
        // URL 匹配失败时，按展示名称匹配
        if (!matched && displayName) {
          matched = allTracks.value.find((t) => t.name === displayName);
        }
        selectedId.value = matched ? matched.id : UPLOAD_MUSIC;
      }
    }
  );

  watch([() => props.modelValue, isMobile], ([visible, mobile]) => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = visible && mobile ? 'hidden' : '';
  });

  const handleViewportChange = () => {
    isMobile.value = isMobileViewport();
  };

  onMounted(() => {
    isMobile.value = isMobileViewport();
    window.addEventListener('resize', handleViewportChange);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleViewportChange);
    stopAudition();
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  });

  // ============================================================
  // 事件处理
  // ============================================================

  function selectMusic(id: string | null) {
    if (id === UPLOAD_MUSIC) {
      if (!userStore.isPremiumUser) {
        showUpgrade();
        return;
      }
      fileInputRef.value?.click();
      return;
    }
    selectedId.value = id;
  }

  async function handleFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      input.value = '';
      return;
    }

    try {
      const url = await uploadFile(file);
      stopAudition();
      emit('confirm', {
        id: UPLOAD_MUSIC,
        volume: localVolume.value,
        file,
        contentUrl: url,
        trackName: file.name
      });
      emit('update:modelValue', false);
    } catch (err) {
      console.error('[MusicSelectDialog] 文件上传失败:', err);
    } finally {
      input.value = '';
    }
  }

  function handleConfirm() {
    const track =
      selectedId.value && selectedId.value !== UPLOAD_MUSIC
        ? allTracks.value.find((t) => t.id === selectedId.value)
        : undefined;

    stopAudition();
    emit('confirm', {
      id: selectedId.value,
      volume: localVolume.value,
      trackName: track?.name,
      track
    });
    emit('update:modelValue', false);
  }
</script>

<style scoped>
  /* ============================================================
   弹窗整体
   ============================================================ */
  .music-select-dialog :deep(.el-dialog__header) {
    padding: 24px 24px 0;
    margin: 0;
  }

  .music-select-dialog :deep(.el-dialog__body) {
    padding: 16px 24px 0;
  }

  .music-select-dialog :deep(.el-dialog__footer) {
    padding: 0 24px 24px;
  }

  /* ============================================================
   自定义头部
   ============================================================ */
  .dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .dialog-title {
    font-size: 16px;
    font-weight: 400;
    color: #333940;
    line-height: 24px;
  }

  .btn-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    color: #333940;
  }

  .btn-close:hover {
    background: #f1f5f9;
  }

  /* ============================================================
   分类标签
   ============================================================ */
  .category-tabs {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 12px;
    padding: 0 7px;
  }

  .category-tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 11px 7px;
    border: none;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    font-size: 15px;
    font-weight: 500;
    color: #6b7280;
    line-height: 17px;
    position: relative;
    transition: color 0.2s;
  }

  .category-tab::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 24px;
    height: 2px;
    border-radius: 9999px;
    background: transparent;
    transition: background-color 0.2s;
  }

  .category-tab.active {
    color: #6033e1;
  }

  .category-tab.active::after {
    background: #6033e1;
  }

  .category-tab:hover {
    color: #6033e1;
  }

  /* ============================================================
   音乐滚动列表
   ============================================================ */
  .music-scroll {
    height: 468px;
    margin-bottom: 16px;
  }

  .music-scroll :deep(.list-container) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  /* ============================================================
   音量控制
   ============================================================ */
  .volume-control {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 464px;
    height: 24px;
    margin-bottom: 10px;
  }

  .volume-label {
    font-size: 14px;
    font-weight: 400;
    color: #333333;
    opacity: 0.8;
    line-height: 22px;
    flex-shrink: 0;
  }

  .volume-slider-wrapper {
    width: 380px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  /* el-slider 覆盖为 Figma 样式 */
  .volume-slider {
    width: 380px;
  }

  .volume-slider :deep(.el-slider__runway) {
    height: 6px;
    border-radius: 13px;
    background-color: #e4e7ed;
  }

  .volume-slider :deep(.el-slider__bar) {
    height: 6px;
    border-radius: 13px;
    background-color: #6033e1;
  }

  .volume-slider :deep(.el-slider__button) {
    width: 14px !important;
    height: 14px !important;
    border: 2px solid #6033e1;
    background-color: #ffffff;
    box-shadow: none;
  }

  .volume-slider :deep(.el-slider__button:hover),
  .volume-slider :deep(.el-slider__button:active),
  .volume-slider :deep(.el-slider__button.is-dragging) {
    transform: none;
  }

  .volume-value {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 24px;
    background: #f4f5f7;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 400;
    color: #333333;
    opacity: 0.8;
    flex-shrink: 0;
  }

  /* ============================================================
   底部按钮
   ============================================================ */
  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .btn-cancel {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 24px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    color: #333333;
    line-height: 22px;
  }

  .btn-cancel:hover {
    border-color: #c2c2c2;
    background: #f8fafc;
  }

  .btn-confirm {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 24px;
    border: none;
    border-radius: 6px;
    background: #6033e1;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    color: #ffffff;
    line-height: 22px;
  }

  .btn-confirm:hover {
    background: #7c5cec;
  }

  /* ============================================================
   Mobile bottom sheet
   ============================================================ */
  .music-sheet-root {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .music-sheet-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
  }

  .music-sheet {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: min(780px, 92dvh);
    padding: 16px 16px 0;
    border-radius: 16px 16px 0 0;
    background: #ffffff;
    box-sizing: border-box;
    overflow: hidden;
  }

  .music-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 0 0 auto;
    height: 24px;
    margin-bottom: 16px;
  }

  .music-sheet-title {
    color: #160211;
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
  }

  .music-sheet-header-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .music-sheet-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #160211;
    cursor: pointer;
  }

  .music-sheet-collapse {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 24px;
    padding: 0;
    border: 1px solid #eff0f7;
    border-radius: 66px;
    background: #ffffff;
    cursor: pointer;
  }

  .music-sheet-collapse-icon {
    width: 8px;
    height: 8px;
    display: block;
    border-right: 1.5px solid #160211;
    border-bottom: 1.5px solid #160211;
    transform: rotate(45deg) translateY(-2px);
  }

  .music-sheet-search-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    height: 36px;
    margin-bottom: 12px;
    padding: 0 12px;
    border-radius: 66px;
    background: #eff0f7;
    color: #6e6a7c;
  }

  .music-sheet-search-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #160211;
    font: inherit;
    font-size: 13px;
    line-height: 16px;
  }

  .music-sheet-search-input::placeholder {
    color: #6e6a7c;
  }

  .music-sheet-search-clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #6e6a7c;
    cursor: pointer;
  }

  .music-sheet-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex: 0 0 auto;
    height: 36px;
    margin-bottom: 12px;
  }

  .music-sheet-tabs {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .music-sheet-tabs::-webkit-scrollbar {
    display: none;
  }

  .music-sheet-tab {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 auto;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #160211;
    font: inherit;
    font-size: 13px;
    font-weight: 400;
    line-height: 14px;
    white-space: nowrap;
    cursor: pointer;
  }

  .music-sheet-tab::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    width: 24px;
    height: 2px;
    border-radius: 0;
    background: transparent;
    transform: translateX(-50%);
  }

  .music-sheet-tab.active {
    color: #6033e1;
  }

  .music-sheet-tab.active::after {
    background: #6033e1;
  }

  .music-sheet-upload {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: 0 0 auto;
    height: 36px;
    padding: 0 16px;
    border: 1px solid #eff0f7;
    border-radius: 66px;
    background: #ffffff;
    color: #160211;
    font: inherit;
    font-size: 13px;
    font-weight: 400;
    line-height: 14px;
    white-space: nowrap;
    cursor: pointer;
  }

  .music-sheet-upload .membership-badge {
    position: relative;
    display: inline-block;
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    overflow: hidden;
    border-radius: 4px;
    transition: width 0.22s ease;
  }

  .music-sheet-upload .membership-badge__compact,
  .music-sheet-upload .membership-badge__full {
    position: absolute;
    top: 0;
    left: 0;
    height: 16px;
    max-width: none;
    display: block;
    pointer-events: none;
    transition: opacity 0.18s ease;
  }

  .music-sheet-upload .membership-badge__compact {
    width: 16px;
    opacity: 1;
  }

  .music-sheet-upload .membership-badge__full {
    width: 39px;
    opacity: 0;
  }

  .music-sheet-upload:hover .membership-badge--expandable {
    width: 39px;
  }

  .music-sheet-upload:hover .membership-badge--expandable .membership-badge__compact {
    opacity: 0;
  }

  .music-sheet-upload:hover .membership-badge--expandable .membership-badge__full {
    opacity: 1;
  }

  .music-sheet-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 8px;
  }

  .music-sheet-item {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    min-height: 68px;
    padding: 16px;
    border: 2px solid transparent;
    border-radius: 16px;
    background: #fbf9ff;
    color: #160211;
    font: inherit;
    text-align: left;
    box-sizing: border-box;
    cursor: pointer;
  }

  .music-sheet-item.active {
    border-color: #6033e1;
  }

  .music-sheet-no-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex: 0 0 auto;
  }

  .music-sheet-no-svg {
    width: 24px;
    height: 24px;
  }

  .music-sheet-track-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex: 0 0 auto;
    border-radius: 12px;
    background: #160211;
  }

  .music-sheet-item-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  .music-sheet-item-name {
    overflow: hidden;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .music-sheet-item-duration {
    color: #6e6a7c;
    font-size: 11px;
    line-height: 14px;
  }

  .music-sheet-play {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex: 0 0 auto;
    margin-left: auto;
    border-radius: 66px;
    background: #ede8ff;
  }

  .music-sheet-play.playing {
    background: #6033e1;
  }

  .music-sheet-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
  }

  .music-sheet-empty-text {
    color: #6e6a7c;
    font-size: 13px;
    line-height: 16px;
  }

  .music-sheet-footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 0 auto;
    margin: 0 -16px 0;
    padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    background: #ffffff;
  }

  .music-sheet-slider-row {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 26px;
  }

  .music-sheet-slider-label {
    flex: 0 0 auto;
    width: 26px;
    color: #160211;
    font-size: 13px;
    line-height: 14px;
  }

  .music-sheet-slider-track {
    flex: 1;
    min-width: 0;
  }

  .music-sheet-slider {
    width: 100%;
  }

  .music-sheet-slider :deep(.el-slider__runway) {
    height: 4px;
    border-radius: 6px;
    background-color: #ede8ff;
  }

  .music-sheet-slider :deep(.el-slider__bar) {
    height: 4px;
    border-radius: 6px;
    background-color: #6033e1;
  }

  .music-sheet-slider :deep(.el-slider__button) {
    width: 16px !important;
    height: 16px !important;
    border: 2px solid #6033e1;
    background-color: #ffffff;
    box-shadow: none;
  }

  .music-sheet-slider :deep(.el-slider__button:hover),
  .music-sheet-slider :deep(.el-slider__button:active),
  .music-sheet-slider :deep(.el-slider__button.is-dragging) {
    transform: none;
  }

  .music-sheet-slider-value {
    flex: 0 0 auto;
    min-width: 26px;
    color: #160211;
    font-size: 13px;
    line-height: 14px;
    text-align: right;
  }

  .music-sheet-actions {
    display: flex;
    gap: 8px;
  }

  .music-sheet-btn-cancel,
  .music-sheet-btn-confirm {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    height: 42px;
    border-radius: 16px;
    font: inherit;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    cursor: pointer;
  }

  .music-sheet-btn-cancel {
    border: 1px solid #eff0f7;
    background: #ffffff;
    color: #160211;
  }

  .music-sheet-btn-confirm {
    border: 0;
    background: #6033e1;
    color: #ffffff;
  }

  .music-sheet-fade-enter-active,
  .music-sheet-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .music-sheet-fade-enter-active .music-sheet,
  .music-sheet-fade-leave-active .music-sheet {
    transition: transform 0.2s ease;
  }

  .music-sheet-fade-enter-from,
  .music-sheet-fade-leave-to {
    opacity: 0;
  }

  .music-sheet-fade-enter-from .music-sheet,
  .music-sheet-fade-leave-to .music-sheet {
    transform: translateY(100%);
  }
</style>
